import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISystemDialog } from './user.slice';

export interface ISystemDialogWithSeverity extends ISystemDialog {
  severity?:
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'default'
    | null
    | undefined;
  cb?: () => Promise<void>;
  hasLoading?: boolean;
}

interface ApiSlice {
  isMaintenance: boolean;
  isSessionExpired: boolean;
  systemDialogs: ISystemDialogWithSeverity[];
}

const initialState: ApiSlice = {
  isSessionExpired: false,
  isMaintenance: false,
  systemDialogs: [],
};

const isDuplicateDialog = (
  dialogs: ISystemDialogWithSeverity[],
  dialog: ISystemDialogWithSeverity,
): boolean => {
  return dialogs.some(
    (d) => dialog._id && d._id === dialog._id,
    //  ||
    //   d.message.trim() === dialog.message.trim(),
  );
};

export const attachDialogCallback = ({
  dialogs,
  cb,
  hasLoading,
}: {
  dialogs: ISystemDialog[];
  cb: () => Promise<void>;
  hasLoading?: boolean;
}) => {
  const lastIndex = dialogs.map((d) => d.dialog_type).lastIndexOf('Dialog Box');

  return dialogs.map((item, index) =>
    index === lastIndex ? { ...item, cb, hasLoading } : item,
  );
};

export const APISlice = createSlice({
  name: 'apiSlice',
  initialState,
  reducers: {
    /**
     * Reset api slice state
     */
    resetApiSlice: () => initialState,

    /**
     * Update expired session flag
     */
    setExpiredSession: (state, action: PayloadAction<boolean>) => {
      state.isSessionExpired = action.payload;
    },

    /**
     * Update maintenance mode
     */
    setIsMaintenance: (state, action: PayloadAction<boolean>) => {
      state.isMaintenance = action.payload;
    },

    /**
     * Replace all system dialogs
     */
    setSystemDialogs: (
      state,
      action: PayloadAction<ISystemDialogWithSeverity[]>,
    ) => {
      const uniqueDialogs: ISystemDialogWithSeverity[] = [];

      action.payload.forEach((dialog) => {
        if (!isDuplicateDialog(uniqueDialogs, dialog)) {
          uniqueDialogs.push(dialog);
        }
      });

      state.systemDialogs = uniqueDialogs;
    },

    /**
     * Add dialog only if not duplicate
     */
    addSystemDialog: (
      state,
      action: PayloadAction<ISystemDialogWithSeverity>,
    ) => {
      if (!state.systemDialogs) {
        state.systemDialogs = [action.payload];
        return;
      }

      if (!isDuplicateDialog(state.systemDialogs, action.payload)) {
        state.systemDialogs.push(action.payload);
      }
    },

    /**
     * Clear system dialogs
     */
    clearSystemDialogs: (state) => {
      state.systemDialogs = [];
    },
  },
});

export default APISlice.reducer;

export const {
  resetApiSlice,
  setExpiredSession,
  setSystemDialogs,
  addSystemDialog,
  clearSystemDialogs,
  setIsMaintenance,
} = APISlice.actions;
